import { Action, createReducer, on } from '@ngrx/store';
import { NomObjectClass, SortDirection } from '../constants';
import {
  fetchWorkspaceViewConfigSuccess,
  setWorkspaceFilterString,
  setWorkspaceSortingDirection,
  setWorkspaceSortingField,
  setWorkspaceTab,
  toggleWorkspaceViewConfigOption,
} from '../actions/workspace.actions';

import { PropertyViewConfig } from '../services/feed-settings.service';
import { User } from '../models/core.models';
import { loginSuccess } from '../actions/auth.actions';
import { objectChanged } from '../actions/core.actions';
import { produce } from 'immer';

export interface WorkspaceState {
  currentWorkspaceId: string;
  tab: 'active' | 'archived' | 'deleted';
  filterString?: string;
  sortField?: string;
  sortDirection?: SortDirection;
  viewConfig: PropertyViewConfig[];
}

export const initialState: WorkspaceState = {
  currentWorkspaceId: '',
  tab: 'active',
  filterString: '',
  sortField: 'title',
  sortDirection: 'asc',
  viewConfig: [],
};

const _workspaceReducer = createReducer(
  initialState,
  on(setWorkspaceFilterString, (state, { filterString }) => {
    return {
      ...state,
      filterString,
    };
  }),

  on(setWorkspaceSortingField, (state, { sortingField }) => {
    return {
      ...state,
      sortField: sortingField,
    };
  }),

  on(setWorkspaceSortingDirection, (state, { sortingDirection }) => {
    return {
      ...state,
      sortDirection: sortingDirection,
    };
  }),

  on(setWorkspaceTab, (state, { tab }) => {
    return {
      ...state,
      tab,
    };
  }),

  on(objectChanged, (state, { object }) => {
    if (object && object.object_class_id === NomObjectClass.WORKSPACE) {
      return {
        ...state,
        currentWorkspaceId: object.id,
      };
    }

    return state;
  }),

  on(loginSuccess, (state, { response }) => {
    // Safely access nested properties and provide fallback values for undefined fields
    const user = response.user;
    const objectManagerWorkspace =
      user?.context_settings?.['object_manager_workspace'];

    return {
      ...state,
      sortField: objectManagerWorkspace?.sortingField ?? 'title', // fallback to a default if sortingField is missing
      sortDirection: objectManagerWorkspace?.sortingDirection ?? 'asc', // fallback to 'asc' if sortingDirection is missing
    };
  }),

  on(
    fetchWorkspaceViewConfigSuccess,
    (state, { defaultWorkspaceConfig, workspaceConfig }) => {
      return {
        ...state,
        viewConfig: defaultWorkspaceConfig.map((option) => {
          const existingOption = workspaceConfig.find(
            (v) => v.propertyUri === option.propertyUri,
          );

          return {
            ...option,
            selected: option.selected || existingOption?.selected || false,
          };
        }),
      };
    },
  ),

  on(
    toggleWorkspaceViewConfigOption,
    produce((draft, { optionName }) => {
      const option = draft.viewConfig.find((opt) => opt.label === optionName);

      if (!option) return;

      option.selected = !option.selected;
    }),
  ),
);

export function workspaceReducer(state: WorkspaceState, action: Action) {
  return _workspaceReducer(state, action);
}
